// src/pages/MonCompte.jsx
import React, { useEffect, useState } from 'react';
import { adminAPI } from '../services/api';
import { extractError } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  User, Lock, Save, Eye, EyeOff, ShieldCheck,
  ChevronDown, ChevronUp, Phone, Mail, MapPin, Building2,
} from 'lucide-react';

const ROLE_LABELS = {
  admin:              'Business Manager',
  super_admin:        'Super Administrateur',
  business_manager:   "Ingénieur d'Affaires",
  talent_acquisition: 'Talent Acquisition',
  rh:                 'Ressources Humaines',
  referent_technique: 'Référent Technique',
  ingenieur_affaires: "Ingénieur d'Affaires",
};

const ROLE_COLORS = {
  super_admin:        '#7C3AED',
  admin:              '#DC2626',
  business_manager:   '#3B82F6',
  talent_acquisition: '#EA580C',
  rh:                 '#059669',
  referent_technique: '#0891B2',
  ingenieur_affaires: '#3B82F6',
};

const SITE_LABELS = {
  FRANCE:   '🇫🇷 France',
  BELGIQUE: '🇧🇪 Belgique',
  ALGERIE:  '🇩🇿 Algérie',
  TUNISIE:  '🇹🇳 Tunisie',
};

function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background:'var(--bg-card)', borderRadius:12, border:'1px solid var(--border)', overflow:'hidden', marginBottom:16, boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'16px 20px', background:'none', border:'none', cursor:'pointer', borderBottom: open ? '1px solid var(--border)' : 'none' }}
      >
        <div style={{ width:32, height:32, borderRadius:8, background:'#3B82F610', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <Icon size={16} color="#3B82F6"/>
        </div>
        <span style={{ fontWeight:700, fontSize:'0.9375rem', color:'var(--text-primary)', flex:1, textAlign:'left' }}>{title}</span>
        {open ? <ChevronUp size={16} color="var(--text-muted)"/> : <ChevronDown size={16} color="var(--text-muted)"/>}
      </button>
      {open && <div style={{ padding:'20px' }}>{children}</div>}
    </div>
  );
}

function PwdInput({ label, value, onChange, show, onToggle, placeholder }) {
  return (
    <div>
      <label style={{ display:'block', fontSize:'0.8rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:6 }}>{label}</label>
      <div style={{ position:'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={{ width:'100%', padding:'10px 40px 10px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' }}
        />
        <button type="button" onClick={onToggle}
          style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', display:'flex', alignItems:'center' }}>
          {show ? <EyeOff size={16}/> : <Eye size={16}/>}
        </button>
      </div>
    </div>
  );
}

export default function MonCompte() {
  const [profile, setProfile]   = useState({ prenom:'', nom:'', telephone:'', site:'', email:'', role:'' });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving,  setProfileSaving]  = useState(false);
  const [pwd,      setPwd]      = useState({ current_password:'', new_password:'', confirm_password:'' });
  const [pwdSaving,setPwdSaving]= useState(false);
  const [showCur,  setShowCur]  = useState(false);
  const [showNew,  setShowNew]  = useState(false);
  const [showConf, setShowConf] = useState(false);

  useEffect(() => {
    adminAPI.getMe()
      .then(r => setProfile({ prenom:r.data.prenom||'', nom:r.data.nom||'', telephone:r.data.telephone||'', site:r.data.site||'', email:r.data.email||'', role:r.data.role||'' }))
      .catch(e => toast.error(extractError(e)))
      .finally(() => setProfileLoading(false));
  }, []);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      const res = await adminAPI.updateMe({ prenom: profile.prenom||undefined, nom: profile.nom||undefined, telephone: profile.telephone||undefined });
      setProfile(p => ({ ...p, prenom:res.data.prenom||'', nom:res.data.nom||'', telephone:res.data.telephone||'' }));
      toast.success('Profil mis à jour');
    } catch (e) { toast.error(extractError(e)); }
    finally { setProfileSaving(false); }
  };

  const handlePwdSave = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm_password) { toast.error('Les mots de passe ne correspondent pas'); return; }
    if (pwd.new_password.length < 8) { toast.error('Minimum 8 caractères'); return; }
    setPwdSaving(true);
    try {
      await adminAPI.changePassword({ current_password:pwd.current_password, new_password:pwd.new_password, confirm_password:pwd.confirm_password });
      toast.success('Mot de passe modifié');
      setPwd({ current_password:'', new_password:'', confirm_password:'' });
    } catch (e) { toast.error(extractError(e)); }
    finally { setPwdSaving(false); }
  };

  if (profileLoading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', gap:12, color:'var(--text-muted)' }}>
      <div className="spinner"/> Chargement…
    </div>
  );

  const roleKey   = (profile.role || '').toLowerCase();
  const roleLabel = ROLE_LABELS[roleKey] || profile.role;
  const roleColor = ROLE_COLORS[roleKey] || 'var(--text-muted)';
  const initials  = ((profile.prenom?.[0]||'') + (profile.nom?.[0]||'')).toUpperCase() || 'WU';
  const siteLabel = SITE_LABELS[profile.site] || profile.site || '—';

  return (
    <div style={{ maxWidth:680, margin:'0 auto' }}>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Mon compte</h1>
          <p>Gérez vos informations personnelles et votre mot de passe</p>
        </div>
      </div>

      {/* Hero card */}
      <div style={{ background:'linear-gradient(135deg, #1D4ED8 0%, #3B82F6 100%)', borderRadius:16, padding:'24px 28px', marginBottom:20, display:'flex', alignItems:'center', gap:20, color:'white' }}>
        <div style={{ width:64, height:64, borderRadius:'50%', background:'rgba(255,255,255,0.25)', border:'3px solid rgba(255,255,255,0.5)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:'1.5rem', flexShrink:0 }}>
          {initials}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:800, fontSize:'1.25rem', marginBottom:4 }}>{profile.prenom} {profile.nom}</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:10, opacity:0.9, fontSize:'0.875rem', alignItems:'center' }}>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><Mail size={13}/>{profile.email}</span>
            {profile.telephone && <span style={{ display:'flex', alignItems:'center', gap:5 }}><Phone size={13}/>{profile.telephone}</span>}
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><MapPin size={13}/>{siteLabel}</span>
          </div>
        </div>
        <div style={{ padding:'6px 14px', borderRadius:20, background:'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.4)', fontSize:'0.8125rem', fontWeight:700, whiteSpace:'nowrap', flexShrink:0 }}>
          <ShieldCheck size={13} style={{ verticalAlign:'middle', marginRight:5 }}/>{roleLabel}
        </div>
      </div>

      {/* Informations personnelles */}
      <Section title="Informations personnelles" icon={User}>
        <form onSubmit={handleProfileSave}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <div>
              <label style={{ display:'block', fontSize:'0.8rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:6 }}>Prénom</label>
              <input style={{ width:'100%', padding:'10px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' }}
                value={profile.prenom} onChange={e => setProfile(p => ({...p, prenom:e.target.value}))} placeholder="Votre prénom"/>
            </div>
            <div>
              <label style={{ display:'block', fontSize:'0.8rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:6 }}>Nom</label>
              <input style={{ width:'100%', padding:'10px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' }}
                value={profile.nom} onChange={e => setProfile(p => ({...p, nom:e.target.value}))} placeholder="Votre nom"/>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
            <div>
              <label style={{ display:'block', fontSize:'0.8rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:6 }}>Email</label>
              <input style={{ width:'100%', padding:'10px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', background:'var(--gray-50)', color:'var(--text-muted)' }}
                value={profile.email} disabled/>
            </div>
            <div>
              <label style={{ display:'block', fontSize:'0.8rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:6 }}>Téléphone</label>
              <input style={{ width:'100%', padding:'10px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' }}
                value={profile.telephone} onChange={e => setProfile(p => ({...p, telephone:e.target.value}))} placeholder="+33 6 00 00 00 00"/>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:16, borderTop:'1px solid var(--border)' }}>
            <div style={{ display:'flex', gap:12, fontSize:'0.8125rem', color:'var(--text-muted)', alignItems:'center' }}>
              <span style={{ display:'flex', alignItems:'center', gap:4 }}><Building2 size={13}/> {siteLabel}</span>
              <span style={{ whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:20, background:`${roleColor}15`, color:roleColor, fontWeight:700, border:`1px solid ${roleColor}30` }}>
                <ShieldCheck size={12}/> {roleLabel}
              </span>
            </div>
            <button className="btn btn-primary" type="submit" disabled={profileSaving}>
              {profileSaving ? <><div className="spinner" style={{ width:13,height:13 }}/> Enregistrement…</> : <><Save size={13}/> Enregistrer</>}
            </button>
          </div>
        </form>
      </Section>

      {/* Mot de passe */}
      <Section title="Changer le mot de passe" icon={Lock} defaultOpen={false}>
        <form onSubmit={handlePwdSave}>
          <div style={{ marginBottom:16 }}>
            <PwdInput label="Mot de passe actuel" value={pwd.current_password} onChange={e => setPwd(p => ({...p, current_password:e.target.value}))} show={showCur} onToggle={() => setShowCur(v => !v)} placeholder="Votre mot de passe actuel"/>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
            <PwdInput label="Nouveau mot de passe" value={pwd.new_password} onChange={e => setPwd(p => ({...p, new_password:e.target.value}))} show={showNew} onToggle={() => setShowNew(v => !v)} placeholder="Min. 8 caractères"/>
            <PwdInput label="Confirmer" value={pwd.confirm_password} onChange={e => setPwd(p => ({...p, confirm_password:e.target.value}))} show={showConf} onToggle={() => setShowConf(v => !v)} placeholder="Répéter le nouveau mot de passe"/>
          </div>
          {pwd.new_password && pwd.confirm_password && pwd.new_password !== pwd.confirm_password && (
            <div style={{ padding:'8px 12px', background:'rgba(239,68,68,0.12)', border:'1px solid rgba(239,68,68,0.22)', borderRadius:6, fontSize:'0.8125rem', color:'#DC2626', marginBottom:12 }}>
              Les mots de passe ne correspondent pas
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:16, borderTop:'1px solid var(--border)' }}>
            <button className="btn btn-primary" type="submit"
              disabled={pwdSaving || !pwd.current_password || !pwd.new_password || !pwd.confirm_password}>
              {pwdSaving ? <><div className="spinner" style={{ width:13,height:13 }}/> Modification…</> : <><Lock size={13}/> Changer le mot de passe</>}
            </button>
          </div>
        </form>
      </Section>
    </div>
  );
}
